import { Component, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { L10N_LOCALE, L10nTranslationModule } from 'angular-l10n';
import { MatSortModule } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { FileParserFactoryService } from '../file-parser/file-parser-factory.service';

export interface SpreadsheetPreviewDialogData {
  file: File;
  maxPreviewRows?: number;
}

export interface SpreadsheetPreviewData {
  headers: string[];
  rows: string[][];
  totalRows: number;
  truncated: boolean;
}

@Component({
  selector: 'app-spreadsheet-preview-dialog',
  templateUrl: './spreadsheet-preview-dialog.component.html',
  styleUrl: './spreadsheet-preview-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    L10nTranslationModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatChipsModule,
    MatCardModule,
    MatButtonModule,
    RouterModule,
  ],
})
export class SpreadsheetPreviewDialogComponent {
  spreadsheetData = signal<SpreadsheetPreviewData | null>(null);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  public readonly data = inject<SpreadsheetPreviewDialogData>(MAT_DIALOG_DATA);
  public readonly locale = inject(L10N_LOCALE);
  private readonly dialogRef = inject(MatDialogRef<SpreadsheetPreviewDialogComponent>);
  private readonly fileParserFactory = inject(FileParserFactoryService);

  constructor() {
    void this.loadSpreadsheetData();
  }

  /**
   * Loads and parses the spreadsheet file using the appropriate parser
   */
  private async loadSpreadsheetData(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    const maxRows = this.data.maxPreviewRows || 100;

    try {
      if (!this.fileParserFactory.canParse(this.data.file)) {
        const extension = this.data.file.name.split('.').pop()?.toLowerCase() || 'unknown';
        this.error.set(`Unsupported file type: .${extension}`);
        this.loading.set(false);
        return;
      }

      const parser = this.fileParserFactory.getParser(this.data.file);
      const result = await parser.parse(this.data.file, maxRows + 1); // +1 for header

      if (result.errors.length > 0) {
        console.error('Spreadsheet parsing errors:', result.errors);
      }

      const allRows = result.data;

      if (allRows.length === 0) {
        this.error.set('The spreadsheet file appears to be empty.');
        this.loading.set(false);
        return;
      }

      // First row as headers
      const headers = allRows[0];

      // Remaining rows as data (limited to maxRows)
      const dataRows = allRows.slice(1, maxRows + 1);

      // Total rows is all data rows (excluding header)
      const totalDataRows = allRows.length - 1;

      this.spreadsheetData.set({
        headers: headers || [],
        rows: dataRows,
        totalRows: totalDataRows,
        truncated: totalDataRows > maxRows,
      });

      this.loading.set(false);
    } catch (err) {
      console.error('File parsing error:', err);
      this.error.set('Failed to parse spreadsheet file. Please ensure it is a valid CSV or Excel file.');
      this.loading.set(false);
    }
  }

  /**
   * Gets the column label (A, B, C, ... Z, AA, AB, etc.)
   */
  getColumnLabel(index: number): string {
    let label = '';
    let num = index;

    while (num >= 0) {
      label = String.fromCharCode(65 + (num % 26)) + label;
      num = Math.floor(num / 26) - 1;
    }

    return label;
  }

  /**
   * Gets the displayed columns for the mat-table
   */
  getDisplayedColumns(): string[] {
    const data = this.spreadsheetData();
    if (!data) return [];

    const columns = ['rowNumber'];
    for (let i = 0; i < data.headers.length; i++) {
      columns.push('col' + i);
    }
    return columns;
  }

  /**
   * Closes the dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}
